import React, { useState, useEffect } from 'react';
import { Zap, ShoppingBag } from 'lucide-react';
import { Product } from '../../types/store';
import { STORE_CONFIG } from '../../config/storeConfig';
import { StarRating } from './StarRating';
import { getProductMainImage, getCanonicalFallbackImage, CANONICAL_JPG_FALLBACKS, CANONICAL_STATIC_IMAGES } from '../../data/products';

interface ProductCardProps {
  product: Product;
  onSelectProduct?: (product: Product) => void;
  onQuickOrder?: (product: Product) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onSelectProduct,
  onQuickOrder,
}) => {
  const currentPrice = product.price;
  const originalPrice = product.compareAtPrice;
  const currentTitle = product.title;

  const [imgSrc, setImgSrc] = useState<string>(() => getProductMainImage(product));
  const [retryStage, setRetryStage] = useState<number>(0);

  useEffect(() => {
    setImgSrc(getProductMainImage(product));
    setRetryStage(0);
  }, [product]);

  const handleCardClick = () => {
    if (onSelectProduct) {
      onSelectProduct(product);
    }
  };

  const handleOrderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onQuickOrder) {
      onQuickOrder(product);
    } else if (onSelectProduct) {
      onSelectProduct(product);
    }
  };

  const handleImageError = () => {
    if (retryStage === 0) {
      setRetryStage(1);
      setImgSrc(getCanonicalFallbackImage(product.flavor, product.id, product.title));
    } else if (retryStage === 1) {
      setRetryStage(2);
      const target = `${product.flavor || ''} ${product.id || ''} ${product.title || ''}`.toLowerCase();
      if (target.includes('bundle')) {
        setImgSrc(CANONICAL_JPG_FALLBACKS.bundle);
      } else if (target.includes('mango')) {
        setImgSrc(CANONICAL_JPG_FALLBACKS.mango);
      } else if (target.includes('choc')) {
        setImgSrc(CANONICAL_JPG_FALLBACKS.chocolate);
      } else if (target.includes('straw')) {
        setImgSrc(CANONICAL_JPG_FALLBACKS.strawberry);
      } else {
        setImgSrc(CANONICAL_JPG_FALLBACKS.vanilla);
      }
    } else if (retryStage === 2) {
      setRetryStage(3);
      setImgSrc(CANONICAL_STATIC_IMAGES.vanilla);
    }
  };

  return (
    <div
      id={`product-card-${product.id}`}
      onClick={handleCardClick}
      className="group relative bg-white rounded-2xl border border-slate-200/90 hover:border-slate-300 shadow-2xs hover:shadow-lg transition-all duration-300 flex flex-col justify-between h-full cursor-pointer p-3.5 sm:p-4 text-center"
    >
      {/* Top Image Container */}
      <div className="relative w-full rounded-2xl overflow-hidden aspect-square bg-[#F8FAFC] border border-slate-100 flex items-center justify-center">
        <img
          src={imgSrc}
          alt={currentTitle}
          width="400"
          height="400"
          className="w-full h-full object-cover object-center group-hover:scale-102 transition-transform duration-500 ease-out"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={handleImageError}
        />

        {/* Sale Pill Badge */}
        <div className="absolute top-2.5 left-2.5 z-10">
          <span className="bg-[#2563EB] text-white text-[11px] font-bold px-2.5 py-0.5 rounded shadow-2xs">
            Sale
          </span>
        </div>
      </div>

      {/* Info & Pricing Details */}
      <div className="mt-3.5 flex flex-col flex-grow justify-between">
        <div>
          {/* Title */}
          <h3 className="font-bold text-[12px] sm:text-[13px] text-[#111827] group-hover:text-[#191975] transition-colors uppercase tracking-tight line-clamp-2 leading-snug min-h-[38px] flex items-center justify-center">
            {currentTitle}
          </h3>

          {/* Star Rating if available */}
          {product.rating > 0 && product.reviewCount > 0 && (
            <div className="my-1.5 flex items-center justify-center">
              <StarRating rating={product.rating} reviewCount={product.reviewCount} size="sm" />
            </div>
          )}

          {/* Pricing */}
          <div className="mt-1 flex items-center justify-center gap-2 flex-wrap text-xs sm:text-sm">
            {originalPrice > currentPrice && (
              <span className="text-slate-400 line-through font-normal text-xs sm:text-[13px]">
                {STORE_CONFIG.formatPrice(originalPrice)}
              </span>
            )}
            <span className="font-bold text-[#111827] text-xs sm:text-sm lg:text-[15px]">
              {STORE_CONFIG.formatPrice(currentPrice)}
            </span>
          </div>
        </div>

        {/* Full-width PLACE ORDER (COD) Pill Button */}
        <div className="mt-3.5 pt-1">
          <button
            id={`place-order-btn-${product.id}`}
            onClick={handleOrderClick}
            className="w-full py-3 px-4 rounded-full font-extrabold text-xs sm:text-[13px] tracking-wider uppercase flex items-center justify-center gap-1.5 transition-all duration-200 shadow-md bg-emerald-600 hover:bg-emerald-500 text-white hover:shadow-lg active:scale-[0.98] cursor-pointer"
          >
            <Zap className="w-4 h-4 fill-white" />
            <span>PLACE ORDER (COD)</span>
          </button>
        </div>
      </div>
    </div>
  );
};


